import React, { useState, useEffect, useRef } from 'react';
import {
  Table, Button, Modal, Form, Input, message,
  Card, Tag, Space, Popconfirm
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EyeOutlined, EditOutlined,
  BookOutlined, LinkOutlined, UploadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../utils/api';

const { TextArea } = Input;

const CreativeCopyLibrary = () => {
  const [copies, setCopies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [editingCopy, setEditingCopy] = useState(null);
  const [selectedCopy, setSelectedCopy] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const fileInputRef = useRef(null);

  const fetchCopies = async (page = 1, pageSize = 10, search = searchText) => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) {
        params.search = search;
      }
      const response = await api.get('/creative-copy/', { params });
      const data = response.data || {};
      setCopies(data.items || []);
      setPagination({
        current: data.page || page,
        pageSize: data.page_size || pageSize,
        total: data.total || 0,
      });
    } catch (error) {
      message.error('获取文案库列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCopies(1, pagination.pageSize, searchText);
  }, [searchText, pagination.pageSize]);

  const handleAdd = () => {
    setEditingCopy(null);
    setModalVisible(true);
    form.resetFields();
    form.setFieldsValue({
      headlines: ['', '', ''],
      bodies: [''],
      descriptions: ['', '']
    });
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: ['正文', '标题', '描述'] });

        const bodies = [];
        const headlines = [];
        const descriptions = [];

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row['正文'] && row['正文'].trim()) {
            bodies.push(row['正文'].trim());
          }
          if (row['标题'] && row['标题'].trim()) {
            headlines.push(row['标题'].trim());
          }
          if (row['描述'] && row['描述'].trim()) {
            descriptions.push(row['描述'].trim());
          }
        }

        if (bodies.length === 0 && headlines.length === 0 && descriptions.length === 0) {
          message.warning('Excel文件中没有有效数据');
          return;
        }

        form.setFieldsValue({
          bodies: bodies.length > 0 ? bodies : [''],
          headlines: headlines.length > 0 ? headlines : ['', '', ''],
          descriptions: descriptions.length > 0 ? descriptions : ['', '']
        });

        message.success('导入成功');
      } catch (error) {
        message.error('导入失败，请确保文件格式正确');
        console.error('Import error:', error);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleEdit = (copy) => {
    setEditingCopy(copy);
    setModalVisible(true);
    form.setFieldsValue({
      book_name: copy.book_name,
      book_id: copy.book_id,
      promotion_link: copy.promotion_link,
      headlines: [...copy.headlines],
      bodies: [...copy.bodies],
      descriptions: [...copy.descriptions]
    });
  };

  const handleView = (copy) => {
    setSelectedCopy(copy);
    setDetailVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/creative-copy/${id}`);
      message.success('删除成功');
      fetchCopies(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        book_name: values.book_name,
        book_id: values.book_id || null,
        promotion_link: values.promotion_link || null,
        bodies: values.bodies.filter(item => item.trim()),
        headlines: values.headlines.filter(item => item.trim()),
        descriptions: values.descriptions.filter(item => item.trim())
      };

      if (editingCopy) {
        await api.put(`/creative-copy/${editingCopy.id}`, data);
        message.success('更新成功');
      } else {
        await api.post('/creative-copy/', data);
        message.success('创建成功');
      }

      setModalVisible(false);
      form.resetFields();
      fetchCopies(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
    }
  };

  const addField = (fieldName) => {
    form.setFieldsValue({
      [fieldName]: [...(form.getFieldValue(fieldName) || []), '']
    });
  };

  const removeField = (fieldName, index) => {
    const values = form.getFieldValue(fieldName) || [];
    if (values.length > 1) {
      values.splice(index, 1);
      form.setFieldsValue({ [fieldName]: values });
    }
  };

  const columns = [
    {
      title: '书名',
      dataIndex: 'book_name',
      key: 'book_name',
      ellipsis: true,
      width: 150
    },
    {
      title: '书籍ID',
      dataIndex: 'book_id',
      key: 'book_id',
      ellipsis: true,
      width: 120
    },
    {
      title: '正文数量',
      dataIndex: 'bodies',
      key: 'bodies',
      width: 80,
      render: (bodies) => bodies.length
    },
    {
      title: '标题数量',
      dataIndex: 'headlines',
      key: 'headlines',
      width: 80,
      render: (headlines) => headlines.length
    },
    {
      title: '描述数量',
      dataIndex: 'descriptions',
      key: 'descriptions',
      width: 80,
      render: (descriptions) => descriptions.length
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'gray'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time) => new Date(time).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(record)}
          >
            查看
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此文案库？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BookOutlined />
            <span>文案库管理</span>
          </div>
        }
        extra={
          <div style={{ display: 'flex', gap: 10 }}>
            <Input.Search
              placeholder="搜索书名"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新建文案库
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={copies}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              fetchCopies(page, pageSize);
            },
          }}
        />
      </Card>

      <Modal
        title={editingCopy ? '编辑文案库' : '新建文案库'}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={700}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setModalVisible(false)}>
              取消
            </Button>
            <Button type="primary" htmlType="submit" form="creative-copy-form" style={{ marginLeft: 8 }}>
              {editingCopy ? '保存修改' : '创建'}
            </Button>
          </div>
        }
      >
        <Form
          id="creative-copy-form"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="book_name"
            label="书名"
            rules={[{ required: true, message: '请输入书名' }]}
          >
            <Input placeholder="请输入书名" />
          </Form.Item>

          <Form.Item
            name="book_id"
            label="书籍ID"
          >
            <Input placeholder="请输入书籍外部ID（可选）" />
          </Form.Item>

          <Form.Item
            name="promotion_link"
            label="推广链接"
          >
            <Input
              placeholder="请输入推广链接（可选）"
              prefix={<LinkOutlined />}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Button
                type="dashed"
                icon={<UploadOutlined />}
                onClick={() => fileInputRef.current?.click()}
              >
                导入XLSX文件
              </Button>
              <span style={{ color: '#999', fontSize: 12 }}>
                格式：A列正文，B列标题，C列描述（第一行为表头）
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImport}
                style={{ display: 'none' }}
              />
            </div>
          </Form.Item>

          <Form.Item label="标题（支持表情）">
            <Form.List name="headlines">
              {(fields, { add, remove }) => (
                <div>
                  {fields.map((field, index) => (
                    <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8, width: '100%' }}>
                      <div style={{ flex: 1 }}>
                        <Form.Item
                          {...field}
                          rules={[{ required: true, message: '请输入标题' }]}
                        >
                          <Input
                            placeholder={`标题 ${index + 1}`}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          onClick={() => remove(field.name)}
                          style={{ alignSelf: 'center', flexShrink: 0 }}
                        >
                          <DeleteOutlined />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加标题
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item label="正文">
            <Form.List name="bodies">
              {(fields, { add, remove }) => (
                <div>
                  {fields.map((field, index) => (
                    <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8, width: '100%' }}>
                      <div style={{ flex: 1 }}>
                        <Form.Item
                          {...field}
                          rules={[{ required: true, message: '请输入正文' }]}
                        >
                          <TextArea
                            rows={3}
                            placeholder={`正文 ${index + 1}`}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          onClick={() => remove(field.name)}
                          style={{ alignSelf: 'flex-start', marginTop: 8, flexShrink: 0 }}
                        >
                          <DeleteOutlined />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加正文
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item label="描述（支持表情）">
            <Form.List name="descriptions">
              {(fields, { add, remove }) => (
                <div>
                  {fields.map((field, index) => (
                    <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8, width: '100%' }}>
                      <div style={{ flex: 1 }}>
                        <Form.Item
                          {...field}
                          rules={[{ required: true, message: '请输入描述' }]}
                        >
                          <TextArea
                            rows={2}
                            placeholder={`描述 ${index + 1}`}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          onClick={() => remove(field.name)}
                          style={{ alignSelf: 'flex-start', marginTop: 8, flexShrink: 0 }}
                        >
                          <DeleteOutlined />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加描述
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>

          
        </Form>
      </Modal>

      <Modal
        title="文案库详情"
        visible={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {selectedCopy && (
          <div>
            <div className="detail-section">
              <h4>基本信息</h4>
              <div className="detail-row">
                <span className="detail-label">书名：</span>
                <span>{selectedCopy.book_name}</span>
              </div>
              {selectedCopy.book_id && (
                <div className="detail-row">
                  <span className="detail-label">书籍ID：</span>
                  <span>{selectedCopy.book_id}</span>
                </div>
              )}
              {selectedCopy.promotion_link && (
                <div className="detail-row">
                  <span className="detail-label">推广链接：</span>
                  <a href={selectedCopy.promotion_link} target="_blank" rel="noopener noreferrer">
                    {selectedCopy.promotion_link}
                  </a>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">状态：</span>
                <Tag color={selectedCopy.status === 'active' ? 'green' : 'gray'}>
                  {selectedCopy.status === 'active' ? '启用' : '禁用'}
                </Tag>
              </div>
            </div>

            <div className="detail-section">
              <h4>标题 ({selectedCopy.headlines.length}个)</h4>
              <ul>
                {selectedCopy.headlines.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="detail-section">
              <h4>正文 ({selectedCopy.bodies.length}个)</h4>
              <ul>
                {selectedCopy.bodies.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="detail-section">
              <h4>描述 ({selectedCopy.descriptions.length}个)</h4>
              <ul>
                {selectedCopy.descriptions.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <style>{`
              .detail-section {
                margin-bottom: 20px;
                padding-bottom: 15px;
                border-bottom: 1px solid #f0f0f0;
              }
              .detail-section:last-child {
                border-bottom: none;
              }
              .detail-section h4 {
                margin-bottom: 10px;
                font-weight: 600;
              }
              .detail-row {
                margin-bottom: 8px;
              }
              .detail-label {
                display: inline-block;
                width: 80px;
                color: #888;
              }
              ul {
                margin: 0;
                padding-left: 20px;
              }
              li {
                margin-bottom: 5px;
                word-break: break-all;
              }
            `}</style>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CreativeCopyLibrary;